/**
 * Bling Full AI Workspace — premium product-aware commerce assistant
 *
 * Uses position:fixed for guaranteed viewport fitting.
 * Relies on BlingAIRender for all message rendering.
 */
class BlingAIWorkspace extends HTMLElement {
  constructor() {
    super();
    this.config = null;
    this.messages = [];
    this.sessionId = null;
    this.conversationId = null;
    this.selectedProduct = null;
    this.productDetail = null;
    this.pendingImages = [];
    this.sending = false;
    this.productCache = {};
    this.catalogProducts = [];
    this.catalogOffset = 0;
    this.catalogTotal = 0;
    this.catalogLoading = false;
    this.catalogHasMore = true;
    this.activeProduct = null;   // pushed-to-chat product (current AI context)
    this.livePreviewUrl = null;  // image shown in right-side preview panel
    this._scrollLockApplied = false;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this._releaseScrollLock();
  }

  async init() {
    const el = document.getElementById(this.dataset.config);
    if (!el) return;
    try { this.config = JSON.parse(el.textContent); } catch { return; }

    this.sessionId = localStorage.getItem('bling_session_id') || crypto.randomUUID();
    localStorage.setItem('bling_session_id', this.sessionId);
    this.conversationId = localStorage.getItem('bling_conversation_id');

    const params = new URLSearchParams(window.location.search);
    const qProduct = params.get('product_id');
    const initialId = qProduct || this.config.initialProductId;

    this._applyThemeVars();
    this._lockScroll();
    this.renderShell();

    window.addEventListener('resize', () => this._sizeWorkspace());
    window.addEventListener('orientationchange', () => setTimeout(() => this._sizeWorkspace(), 80));

    if (initialId) {
      await this.selectProduct(String(initialId), this.config.initialProductTitle);
    } else {
      this.loadCatalog(true);
    }

    if (this.conversationId) {
      await this.loadHistory();
    } else if (this.config.welcomeMessage) {
      this.messages.push({ role: 'assistant', content: this.config.welcomeMessage });
      await this.renderMessages();
    }
  }

  // ───── Viewport lock ─────────────────────────────────────────
  _lockScroll() {
    if (this._scrollLockApplied) return;
    this._scrollLockApplied = true;
    document.documentElement.dataset.blingWsLock = '1';
    document.body.dataset.blingWsLock = '1';
    // Hide footer
    document.querySelectorAll('footer.footer, .footer, .shopify-section-group-footer-group').forEach(
      (el) => { el.setAttribute('data-bling-hidden', '1'); el.style.display = 'none'; },
    );
    this._sizeWorkspace();
  }

  _releaseScrollLock() {
    if (!this._scrollLockApplied) return;
    delete document.documentElement.dataset.blingWsLock;
    delete document.body.dataset.blingWsLock;
    document.querySelectorAll('[data-bling-hidden="1"]').forEach((el) => {
      el.style.display = '';
      el.removeAttribute('data-bling-hidden');
    });
    this._scrollLockApplied = false;
  }

  _sizeWorkspace() {
    const header = document.querySelector(
      '.header-wrapper, .shopify-section-header, #shopify-section-header, header[role="banner"], header.header',
    );
    const headerH = header ? Math.round(header.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--bai-header-h', headerH + 'px');
  }

  // ───── Theme variables → CSS custom properties ────────────────
  _applyThemeVars() {
    const t = this.config.theme || {};
    const root = this.style;
    const v = (k, fallback) => t[k] || fallback;
    root.setProperty('--bai-bg', v('bg', '#f8f9fb'));
    root.setProperty('--bai-surface', v('surface', '#ffffff'));
    root.setProperty('--bai-sidebar', v('sidebar', '#ffffff'));
    root.setProperty('--bai-border', v('border', '#e4e4e7'));
    root.setProperty('--bai-input-bg', v('inputBg', '#f4f4f5'));
    root.setProperty('--bai-text', v('text', '#18181b'));
    root.setProperty('--bai-muted', v('muted', '#71717a'));
    root.setProperty('--bai-accent', v('accent', '#18181b'));
    root.setProperty('--bai-accent-fg', v('accentFg', '#ffffff'));
    root.setProperty('--bai-radius', v('radius', '12px'));
    // Renderer vars (inherited by all .bai-* elements)
    root.setProperty('--bai-user-bg', v('userBubble', '#18181b'));
    root.setProperty('--bai-user-fg', v('userBubbleFg', '#ffffff'));
    root.setProperty('--bai-assistant-bg', v('assistantBubble', '#ffffff'));
    root.setProperty('--bai-assistant-fg', v('assistantBubbleFg', '#18181b'));
    root.setProperty('--bai-card-bg', v('cardBg', '#ffffff'));
    root.setProperty('--bai-card-fg', v('cardFg', '#18181b'));
    root.setProperty('--bai-card-meta', v('cardMeta', '#71717a'));
    root.setProperty('--bai-card-radius', v('cardRadius', '12px'));
    root.setProperty('--bai-cart', v('cartBtn', '#16a34a'));
    root.setProperty('--bai-buynow', v('buyNowBtn', '#2563eb'));
    root.setProperty('--bai-ghost-bg', v('ghostBg', '#f4f4f5'));
    root.setProperty('--bai-ghost-fg', v('ghostFg', '#18181b'));
    root.setProperty('--bai-preview-bg', v('previewBg', '#fafafa'));
    root.setProperty('--bai-preview-border', v('previewBorder', '#d4d4d8'));
    root.setProperty('--bai-code-bg', v('codeBg', '#f1f5f9'));
  }

  renderShell() {
    this.innerHTML = `
      <style>
        /* Lock outer page scroll */
        html[data-bling-ws-lock], body[data-bling-ws-lock]{height:100%;overflow:hidden!important;overscroll-behavior:none}
        body[data-bling-ws-lock]{padding-bottom:0!important;margin-bottom:0!important}

        bling-ai-workspace{
          position:fixed;
          inset:var(--bai-header-h, 0px) 0 0 0;
          display:block;
          z-index:5;
          background:var(--bai-bg);
          color:var(--bai-text);
          font-family:var(--font-body-family,system-ui,-apple-system,sans-serif);
        }
        .ws-root{display:grid;grid-template-columns:minmax(260px,300px) 1fr;height:100%;min-height:0}
        .ws-sidebar{display:flex;flex-direction:column;gap:10px;padding:16px;border-right:1px solid var(--bai-border);background:var(--bai-sidebar);min-height:0;overflow:hidden}
        .ws-sidebar h2{margin:0;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:var(--bai-muted)}
        .ws-search{width:100%;padding:9px 12px;border-radius:10px;border:1px solid var(--bai-border);background:var(--bai-surface);color:var(--bai-text);font-size:13px;box-sizing:border-box;outline:none;font-family:inherit}
        .ws-search:focus{border-color:var(--bai-accent)}
        .ws-products{flex:0 0 auto;max-height:240px;overflow-y:auto;scroll-behavior:smooth}
        .ws-products-sentinel{height:1px}
        .ws-catalog-meta{font-size:10px;color:var(--bai-muted);padding:4px 6px 0}
        .ws-product-btn{display:flex;align-items:center;gap:8px;width:100%;text-align:left;padding:7px 10px;margin-bottom:3px;border:none;border-radius:8px;background:transparent;color:var(--bai-text);cursor:pointer;font-size:12px;font-family:inherit;transition:background .1s}
        .ws-product-btn:hover{background:var(--bai-input-bg)}
        .ws-product-btn.active{background:var(--bai-input-bg);font-weight:600}
        .ws-product-thumb{width:28px;height:28px;border-radius:6px;object-fit:cover;flex-shrink:0;background:var(--bai-input-bg)}
        .ws-product-name{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .ws-ctx{flex:1;min-height:0;overflow-y:auto;background:var(--bai-surface);border:1px solid var(--bai-border);border-radius:12px;padding:12px}
        .ws-ctx-empty{font-size:12px;color:var(--bai-muted);line-height:1.6}
        .ws-ctx-img{width:100%;border-radius:10px;aspect-ratio:1;object-fit:cover;background:var(--bai-input-bg);margin-bottom:10px}
        .ws-ctx-title{font-size:15px;font-weight:700;margin:0 0 5px;color:var(--bai-text);line-height:1.3}
        .ws-ctx-meta{font-size:11px;color:var(--bai-muted);margin-bottom:8px;line-height:1.5}
        .ws-tag{display:inline-block;font-size:10px;padding:2px 7px;border-radius:4px;background:var(--bai-input-bg);margin:2px 3px 0 0;color:var(--bai-text)}
        .ws-zone{font-size:11px;padding:6px 9px;background:var(--bai-input-bg);border-radius:6px;margin-top:4px;color:var(--bai-text)}
        .ws-main{display:flex;flex-direction:column;min-height:0;min-width:0;background:var(--bai-bg)}
        .ws-header{flex:0 0 auto;padding:14px 22px;border-bottom:1px solid var(--bai-border);font-size:13px;font-weight:700;display:flex;align-items:center;gap:8px;color:var(--bai-text);background:var(--bai-surface)}
        .ws-header-dot{width:8px;height:8px;border-radius:50%;background:#22c55e;flex-shrink:0;box-shadow:0 0 0 3px rgba(34,197,94,.18)}
        .ws-msgs{flex:1;min-height:0;overflow-y:auto;padding:24px;display:flex;flex-direction:column;gap:16px;scroll-behavior:smooth}
        .ws-msgs::-webkit-scrollbar{width:8px}
        .ws-msgs::-webkit-scrollbar-thumb{background:var(--bai-border);border-radius:4px}
        .ws-composer{flex:0 0 auto;padding:14px 18px 18px;border-top:1px solid var(--bai-border);background:var(--bai-surface)}
        .ws-attachments{display:flex;gap:7px;flex-wrap:wrap;margin-bottom:10px}
        .ws-attach-thumb{position:relative;width:56px;height:56px;border-radius:8px;border:1px solid var(--bai-border);overflow:hidden}
        .ws-attach-thumb img{width:100%;height:100%;object-fit:cover;display:block}
        .ws-attach-remove{position:absolute;top:2px;right:2px;width:18px;height:18px;border-radius:50%;background:rgba(0,0,0,.65);color:#fff;border:none;font-size:10px;cursor:pointer;line-height:1}
        .ws-input-row{display:flex;gap:8px;align-items:flex-end}
        .ws-input{flex:1;min-height:46px;max-height:140px;padding:12px 14px;border-radius:var(--bai-radius);border:1px solid var(--bai-border);background:var(--bai-input-bg);color:var(--bai-text);font-size:14px;line-height:1.45;resize:none;outline:none;font-family:inherit;transition:border-color .15s,background .15s}
        .ws-input:focus{border-color:var(--bai-accent);background:var(--bai-surface)}
        .ws-icon-btn{flex-shrink:0;height:46px;min-width:46px;border-radius:var(--bai-radius);border:1px solid var(--bai-border);cursor:pointer;font-size:18px;background:var(--bai-surface);color:var(--bai-text);transition:background .1s,border-color .1s}
        .ws-icon-btn:hover{background:var(--bai-input-bg);border-color:var(--bai-accent)}
        .ws-send{flex-shrink:0;height:46px;border-radius:var(--bai-radius);border:none;cursor:pointer;font-size:13px;font-weight:700;background:var(--bai-accent);color:var(--bai-accent-fg);padding:0 22px;transition:opacity .1s;font-family:inherit}
        .ws-send:hover:not(:disabled){opacity:.9}
        .ws-send:disabled{opacity:.4;cursor:not-allowed}
        .ws-typing{font-size:13px;color:var(--bai-muted);padding:6px 0;animation:ws-blink 1.2s infinite}
        @keyframes ws-blink{0%,100%{opacity:.45}50%{opacity:1}}
        /* ── Right preview panel ── */
        .ws-preview{display:none}
        .ws-preview-head{flex:0 0 auto;padding:12px 14px 8px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--bai-muted);border-bottom:1px solid var(--bai-border)}
        .ws-preview-body{flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding:14px;gap:12px}
        .ws-preview-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:10px;color:var(--bai-muted);text-align:center;padding:20px}
        .ws-preview-empty-icon{font-size:32px;opacity:.4}
        .ws-preview-empty-text{font-size:12px;line-height:1.5;max-width:180px}
        .ws-preview-img{width:100%;border-radius:10px;border:1px solid var(--bai-border);display:block;background:var(--bai-input-bg)}
        .ws-preview-skeleton{width:100%;aspect-ratio:1;border-radius:10px;background:linear-gradient(90deg,var(--bai-input-bg) 25%,var(--bai-border) 50%,var(--bai-input-bg) 75%);background-size:200% 100%;animation:ws-skeleton 1.4s infinite}
        @keyframes ws-skeleton{0%{background-position:200% 0}100%{background-position:-200% 0}}
        .ws-preview-label{font-size:11px;color:var(--bai-muted);text-align:center;line-height:1.4}
        .ws-preview-status{font-size:12px;color:var(--bai-muted);text-align:center;padding:8px;border:1px solid var(--bai-border);border-radius:8px;background:var(--bai-input-bg);width:100%;box-sizing:border-box}
        .ws-preview-actions{display:flex;gap:6px;flex-wrap:wrap;justify-content:center}
        .ws-active-product{margin-top:auto;display:flex;align-items:center;gap:8px;padding:10px;border:1px solid var(--bai-border);border-radius:10px;background:var(--bai-surface);box-shadow:0 1px 3px rgba(0,0,0,.05);width:100%;box-sizing:border-box}
        .ws-active-product-thumb{width:32px;height:32px;border-radius:6px;object-fit:cover;background:var(--bai-input-bg)}
        .ws-active-product-info{flex:1;min-width:0}
        .ws-active-product-label{font-size:9px;font-weight:700;text-transform:uppercase;color:var(--bai-muted);letter-spacing:.05em}
        .ws-active-product-title{font-size:11px;font-weight:600;color:var(--bai-text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}

        @media(max-width:1100px){
          .ws-root{grid-template-columns:minmax(240px,260px) 1fr}
        }
        @media(max-width:849px){
          .ws-root{grid-template-columns:1fr;grid-template-rows:auto 1fr}
          .ws-sidebar{max-height:30vh;border-right:none;border-bottom:1px solid var(--bai-border)}
          .ws-preview{display:none}
          .ws-msgs{padding:14px}
          .ws-composer{padding:10px 12px 14px}
        }
      </style>
      <div class="ws-root">
        <aside class="ws-sidebar">
          <h2>Product context</h2>
          <input class="ws-search" id="ws-search" type="search" placeholder="Search products…" autocomplete="off" />
          <div class="ws-catalog-meta" id="ws-catalog-meta"></div>
          <div class="ws-products" id="ws-products"><div class="ws-products-sentinel" id="ws-products-sentinel"></div></div>
          <div class="ws-ctx" id="ws-ctx"></div>
        </aside>
        <main class="ws-main">
          <div class="ws-header" id="ws-header"><span class="ws-header-dot"></span><span id="ws-header-title">${this.escape(this.config.headerTitle || 'Bling AI — product-aware assistant')}</span></div>
          <div class="ws-msgs" id="ws-msgs"></div>
          <div class="ws-composer">
            <div class="ws-attachments" id="ws-attachments"></div>
            <div class="ws-input-row">
              <input type="file" id="ws-file" accept="image/jpeg,image/png,image/webp" hidden multiple />
              <button type="button" class="ws-icon-btn" id="ws-attach" title="Attach image" aria-label="Attach image">📎</button>
              <textarea class="ws-input" id="ws-input" rows="1" placeholder="${this.escape(this.config.placeholder || 'Ask about gifts, customisation, or upload an image…')}" maxlength="2000"></textarea>
              <button type="button" class="ws-send" id="ws-send">Send</button>
            </div>
          </div>
        </main>
        <aside class="ws-preview" id="ws-preview">
          <div class="ws-preview-head">Live Preview</div>
          <div class="ws-preview-body" id="ws-preview-body">
            <div class="ws-preview-empty">
              <div class="ws-preview-empty-icon">✦</div>
              <p class="ws-preview-empty-text">Push a product to chat, then ask to customise it — previews appear here.</p>
            </div>
          </div>
        </aside>
      </div>
    `;

    this.querySelector('#ws-send').addEventListener('click', () => this.send());
    this.querySelector('#ws-input').addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.send(); }
    });
    this.querySelector('#ws-input').addEventListener('input', (e) => this._autosize(e.target));
    this.querySelector('#ws-attach').addEventListener('click', () => this.querySelector('#ws-file').click());
    this.querySelector('#ws-file').addEventListener('change', (e) => this.onFilesSelected(e));

    let debounce;
    this.querySelector('#ws-search').addEventListener('input', (ev) => {
      clearTimeout(debounce);
      const val = ev.target.value.trim();
      if (!val) {
        this.loadCatalog(true);
        return;
      }
      debounce = setTimeout(() => this.searchCatalog(val), 280);
    });

    const productsBox = this.querySelector('#ws-products');
    if (productsBox) {
      productsBox.addEventListener('scroll', () => this._onProductsScroll());
    }

    const msgsEl = this.querySelector('#ws-msgs');
    msgsEl.addEventListener('click', (e) => this.onMessagesAction(e));
    msgsEl.addEventListener('input', (e) => this.onMessagesAction(e));
    msgsEl.addEventListener('change', (e) => this.onMessagesAction(e));
    this.querySelector('#ws-attachments').addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action="remove-attachment"]');
      if (btn) {
        const idx = parseInt(btn.dataset.idx, 10);
        if (Number.isFinite(idx)) {
          this.pendingImages.splice(idx, 1);
          this.renderAttachmentPreviews();
        }
      }
    });
    this.renderProductContext();
  }

  escape(s) { return window.BlingAIRender ? BlingAIRender.escapeHtml(s) : String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }

  _autosize(el) {
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }

  // ───── Product catalog (full index + search) ─────────────────
  async loadCatalog(reset = false) {
    if (this.catalogLoading) return;
    if (reset) {
      this.catalogOffset = 0;
      this.catalogProducts = [];
      this.catalogHasMore = true;
    }
    if (!this.catalogHasMore && !reset) return;

    this.catalogLoading = true;
    const box = this.querySelector('#ws-products');
    if (reset && box) {
      box.innerHTML = '<p style="font-size:11px;color:var(--bai-muted);padding:8px">Loading catalog…</p><div class="ws-products-sentinel" id="ws-products-sentinel"></div>';
    }

    try {
      const res = await fetch(
        `${this.config.apiBase}/api/ai/products/catalog?limit=30&offset=${this.catalogOffset}`,
      );
      const json = await res.json();
      const data = json.data || {};
      const batch = data.products || [];
      this.catalogTotal = data.total ?? batch.length;
      this.catalogHasMore = data.has_more ?? batch.length >= 30;
      this.catalogOffset += batch.length;
      this.catalogProducts = reset ? batch : [...this.catalogProducts, ...batch];
      this._renderProductButtons(this.catalogProducts);
      this._updateCatalogMeta();
    } catch (e) {
      console.warn('[BlingAI] catalog', e);
      if (reset) this._renderProductButtons([]);
    } finally {
      this.catalogLoading = false;
    }
  }

  async searchCatalog(q) {
    if (!q || q.length < 2) return;
    this.catalogLoading = true;
    const box = this.querySelector('#ws-products');
    if (box) box.innerHTML = '<p style="font-size:11px;color:var(--bai-muted);padding:8px">Searching…</p>';

    try {
      const res = await fetch(
        `${this.config.apiBase}/api/ai/products/catalog?q=${encodeURIComponent(q)}&limit=40&offset=0`,
      );
      const json = await res.json();
      const products = json.data?.products || [];
      this.catalogProducts = products;
      this.catalogOffset = products.length;
      this.catalogTotal = json.data?.total ?? products.length;
      this.catalogHasMore = false;
      this._renderProductButtons(products);
      this._updateCatalogMeta(q);
    } catch (e) {
      console.warn('[BlingAI] catalog search', e);
    } finally {
      this.catalogLoading = false;
    }
  }

  _updateCatalogMeta(searchQ) {
    const meta = this.querySelector('#ws-catalog-meta');
    if (!meta) return;
    if (searchQ) {
      meta.textContent = `${this.catalogProducts.length} result${this.catalogProducts.length !== 1 ? 's' : ''} for “${searchQ}”`;
      return;
    }
    meta.textContent = this.catalogTotal
      ? `${this.catalogProducts.length} of ${this.catalogTotal} products`
      : '';
  }

  _onProductsScroll() {
    const box = this.querySelector('#ws-products');
    if (!box || this.catalogLoading || !this.catalogHasMore) return;
    const searchVal = this.querySelector('#ws-search')?.value?.trim();
    if (searchVal && searchVal.length >= 2) return;
    if (box.scrollTop + box.clientHeight >= box.scrollHeight - 48) {
      this.loadCatalog(false);
    }
  }

  _renderProductButtons(products) {
    const box = this.querySelector('#ws-products');
    if (!box) return;
    if (!products.length) {
      box.innerHTML = '<p style="font-size:11px;color:var(--bai-muted);padding:8px 4px">No products found. Run catalog sync in admin if empty.</p><div class="ws-products-sentinel" id="ws-products-sentinel"></div>';
      return;
    }
    const html = products.map((p) => {
      const active = this.selectedProduct?.id === p.id ? ' active' : '';
      const img = p.image_url
        ? `<img class="ws-product-thumb" src="${this.escape(p.image_url)}" alt="" loading="lazy" />`
        : `<div class="ws-product-thumb"></div>`;
      return `<button type="button" class="ws-product-btn${active}" data-id="${this.escape(p.id)}" data-title="${this.escape(p.title)}">${img}<span class="ws-product-name">${this.escape(p.title)}</span></button>`;
    }).join('');
    box.innerHTML = html + '<div class="ws-products-sentinel" id="ws-products-sentinel"></div>';
    if (this.catalogLoading) {
      box.insertAdjacentHTML('beforeend', '<p style="font-size:10px;color:var(--bai-muted);padding:6px;text-align:center">Loading more…</p>');
    }
    box.querySelectorAll('.ws-product-btn').forEach((btn) =>
      btn.addEventListener('click', () => this.selectProduct(btn.dataset.id, btn.dataset.title)),
    );
  }

  async selectProduct(id, titleHint) {
    this.selectedProduct = { id, title: titleHint || id };
    this.renderProductContext();
    this.querySelector('#ws-products')?.querySelectorAll('.ws-product-btn').forEach((b) => {
      b.classList.toggle('active', b.dataset.id === id);
    });

    try {
      const res = await fetch(`${this.config.apiBase}/api/ai/products/${encodeURIComponent(id)}`);
      const json = await res.json();
      if (json.data?.product) {
        this.productDetail = json.data.product;
        this.selectedProduct = { id: this.productDetail.productId, title: this.productDetail.title };
        this.productCache[id] = {
          id: this.productDetail.productId,
          title: this.productDetail.title,
          handle: this.productDetail.handle,
          imageUrl: this.productDetail.imageUrl,
          price: this.productDetail.price,
          currency: this.productDetail.currency,
          variantId: this.productDetail.variants?.[0]?.variantId || '',
          productUrl: this.productDetail.productUrl,
        };
      }
    } catch (e) {
      console.warn('[BlingAI] product context', e);
    }
    this.renderProductContext();
  }

  renderProductContext() {
    const el = this.querySelector('#ws-ctx');
    if (!el) return;
    const d = this.productDetail;
    if (!d) {
      el.innerHTML = `<p class="ws-ctx-empty">Search or pick a product to unlock product-aware recommendations, customisation help, and live previews. ✦</p>`;
      return;
    }
    const zones = (d.preview?.zones || []).filter((z) => z.type !== 'locked');
    const tags = (d.tags || []).slice(0, 6).map((t) => `<span class="ws-tag">${this.escape(t)}</span>`).join('');
    const fields = (d.customization?.fields || []).slice(0, 6).map((f) =>
      `<div class="ws-zone"><strong>${this.escape(f.label)}</strong> · ${this.escape(f.field_type)}</div>`,
    ).join('');

    const variantId = d.variants?.[0]?.variantId || '';

    el.innerHTML = `
      ${d.imageUrl ? `<img class="ws-ctx-img" src="${this.escape(d.imageUrl)}" alt="" />` : ''}
      <h3 class="ws-ctx-title">${this.escape(d.title)}</h3>
      <p class="ws-ctx-meta">${d.price ? `From ${this.escape(d.price)} ${this.escape(d.currency || '')}` : ''} ${d.customization?.enabled ? '· ✦ Customisable' : ''}</p>
      ${tags ? `<div style="margin-bottom:8px">${tags}</div>` : ''}
      ${d.collections?.length ? `<p class="ws-ctx-meta">Collections: ${this.escape(d.collections.slice(0, 3).join(', '))}</p>` : ''}
      ${fields ? `<div><p class="ws-ctx-meta" style="margin-top:10px;font-weight:600">Customisation fields</p>${fields}</div>` : ''}
      ${zones.length ? `<div><p class="ws-ctx-meta" style="margin-top:10px;font-weight:600">Editable regions</p>${zones.map((z) => `<div class="ws-zone">${this.escape(z.id)} (${this.escape(z.type)})</div>`).join('')}</div>` : ''}
      <div style="display:flex;gap:6px;margin-top:12px;flex-wrap:wrap">
        ${variantId ? `<button type="button" class="bai-btn bai-btn--cart" data-action="add-to-cart" data-variant-id="${this.escape(variantId)}" data-title="${this.escape(d.title)}">Add to cart</button>` : ''}
        <a class="bai-btn bai-btn--ghost" href="${this.escape(d.productUrl)}" target="_blank" rel="noopener">Open product →</a>
        <button type="button" class="bai-btn bai-btn--primary" id="ws-push-to-chat" style="flex:1 0 100%;margin-top:4px">Push to chat ✦</button>
      </div>
    `;

    el.querySelectorAll('[data-action="add-to-cart"]').forEach((btn) =>
      btn.addEventListener('click', (e) => BlingAIRender.handleCardAction(e, { onCartFeedback: (t) => BlingAIRender.toast(`Added ${t} to cart`) })),
    );

    el.querySelector('#ws-push-to-chat')?.addEventListener('click', () => this.pushProductToChat());
  }

  // ───── Push to chat ──────────────────────────────────────────
  pushProductToChat() {
    const d = this.productDetail;
    if (!d) return;

    this.activeProduct = {
      id: d.productId,
      title: d.title,
      handle: d.handle,
      imageUrl: d.imageUrl,
    };
    this.livePreviewUrl = null;
    this.showPreviewEmpty();

    // Update chat header
    const title = this.querySelector('#ws-header-title');
    if (title) title.textContent = d.title;

    // Insert product card into the conversation as an assistant turn
    const productId = d.productId;
    this.productCache[productId] = {
      id: productId,
      title: d.title,
      handle: d.handle,
      imageUrl: d.imageUrl,
      price: d.price,
      currency: d.currency,
      variantId: d.variants?.[0]?.variantId || '',
      productUrl: d.productUrl,
      customization: d.customization || { enabled: false, fields: [] },
      preview: d.preview || { hasTemplate: false, zones: [] },
    };

    this.messages.push({
      role: 'assistant',
      content: `✦ **${d.title}** is now your active product. Ask me anything — to customise, change the style, or explore options:\n\n[[product:${productId}]]`,
    });
    this.renderMessages();
    this.querySelector('#ws-input')?.focus();
    BlingAIRender.toast(`${d.title} is active — type your instructions`);
  }

  // ───── Right-side preview panel ──────────────────────────────
  showPreviewLoading(hint) {
    const body = this.querySelector('#ws-preview-body');
    if (!body) return;
    body.innerHTML = `
      <div class="ws-preview-skeleton"></div>
      <p class="ws-preview-label">${this.escape(hint || 'Generating your preview…')}</p>
    `;
  }

  showPreviewImage(imageUrl, productTitle) {
    this.livePreviewUrl = imageUrl;
    const body = this.querySelector('#ws-preview-body');
    if (!body) return;
    body.innerHTML = `
      <img class="ws-preview-img" src="${this.escape(imageUrl)}" alt="Customised preview" />
      ${productTitle ? `<p class="ws-preview-label">✦ ${this.escape(productTitle)}</p>` : ''}
    `;
  }

  showPreviewError(msg) {
    const body = this.querySelector('#ws-preview-body');
    if (!body) return;
    body.innerHTML = `
      <div class="ws-preview-empty">
        <div class="ws-preview-empty-icon">⚡</div>
        <p class="ws-preview-empty-text">${this.escape(msg || 'Image generation is temporarily unavailable. Please retry in a moment.')}</p>
      </div>
    `;
  }

  showPreviewEmpty() {
    const body = this.querySelector('#ws-preview-body');
    if (!body) return;
    body.innerHTML = `
      <div class="ws-preview-empty">
        <div class="ws-preview-empty-icon">✦</div>
        <p class="ws-preview-empty-text">Push a product to chat, then ask to customise it — previews appear here.</p>
      </div>
    `;
  }

  // ───── Conversation ──────────────────────────────────────────
  async loadHistory() {
    const url = `${this.config.apiBase}/api/ai/conversations/${this.conversationId}/messages?session_id=${encodeURIComponent(this.sessionId)}`;
    try {
      const res = await fetch(url);
      const json = await res.json();
      if (json.data?.messages) {
        this.messages = json.data.messages.filter((m) => m.role !== 'system');
        await this.renderMessages();
      }
    } catch (e) {
      console.warn('[BlingAI] load history', e);
    }
  }

  async renderMessages(opts) {
    const { preserveScroll = false, preserveFocus = true } = opts || {};
    const box = this.querySelector('#ws-msgs');
    if (!box || !window.BlingAIRender) return;
    const shopHost = this.config.shopHost || window.location.hostname;

    // Preserve focused field (text input inside customization drawer)
    let focusedCardKey = null;
    let focusedField = null;
    let focusedSelStart = null;
    if (preserveFocus) {
      const focused = box.contains(document.activeElement) ? document.activeElement : null;
      if (focused && focused.matches('[data-cust-key]')) {
        focusedCardKey = focused.closest('.bai-card')?.dataset.cardKey || null;
        focusedField = focused.dataset.custKey;
        focusedSelStart = focused.selectionStart;
      }
    }

    const prevScroll = box.scrollTop;
    const atBottom = (box.scrollHeight - box.scrollTop - box.clientHeight) < 40;

    const parts = await Promise.all(
      this.messages.map((m, i) =>
        BlingAIRender.renderMessage(m.content, m.role, {
          apiBase: this.config.apiBase,
          shopHost,
          productCache: this.productCache,
          userImageUrl: m.imagePreviewUrl,
          messageKey: `m${i}`,
        }),
      ),
    );
    box.innerHTML = parts.join('');

    if (preserveScroll) {
      box.scrollTop = atBottom ? box.scrollHeight : prevScroll;
    } else {
      box.scrollTop = box.scrollHeight;
    }

    // Restore focus
    if (focusedCardKey && focusedField) {
      const card = box.querySelector(`.bai-card[data-card-key="${CSS.escape(focusedCardKey)}"]`);
      const input = card?.querySelector(`[data-cust-key="${CSS.escape(focusedField)}"]`);
      if (input && input.matches('input[type="text"]')) {
        input.focus();
        if (focusedSelStart != null) try { input.setSelectionRange(focusedSelStart, focusedSelStart); } catch {}
      }
    }
  }

  onMessagesAction(e) {
    if (!window.BlingAIRender) return;
    BlingAIRender.handleCardAction(e, {
      apiBase: this.config.apiBase,
      sessionId: this.sessionId,
      conversationId: this.conversationId,
      activeProduct: this.activeProduct,
      onSelectProduct: (id, title) => this.selectProduct(id, title),
      onCartFeedback: (title) => BlingAIRender.toast(`Added ${title} to cart`),
      onRerender: () => this.renderMessages({ preserveScroll: true, preserveFocus: true }),
      onPreviewLoading: (hint) => this.showPreviewLoading(hint),
      onPreviewGenerated: (imageUrl, productTitle) => this.showPreviewImage(imageUrl, productTitle),
      onPreviewError: (msg) => this.showPreviewError(msg),
    });
  }

  async runPreview(spec, blockEl) {
    if (!this.selectedProduct?.id) {
      BlingAIRender.toast('Select a product first to generate a preview.');
      return;
    }
    const resultEl = blockEl?.querySelector('.bai-preview-result');
    if (resultEl) resultEl.innerHTML = '<span class="bai-preview-loading">Generating your customised preview…</span>';
    try {
      const res = await fetch(`${this.config.apiBase}/api/ai/preview/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopify_product_id: this.selectedProduct.id,
          session_id: this.sessionId,
          zone_values: spec.zone_values || {},
          upload_r2_keys: spec.upload_r2_keys || {},
        }),
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      const poll = await fetch(`${this.config.apiBase}/api/ai/preview/${json.data.preview_id}`);
      const status = await poll.json();
      if (status.data?.output_url && resultEl) {
        resultEl.innerHTML = `<img src="${BlingAIRender.escapeHtml(status.data.output_url)}" alt="Customisation preview" />`;
      } else if (resultEl) {
        resultEl.textContent = status.data?.error_message || 'Preview failed';
      }
    } catch (err) {
      if (resultEl) resultEl.textContent = err.message || 'Preview failed';
    }
  }

  // ───── Uploads ───────────────────────────────────────────────
  renderAttachmentPreviews() {
    const box = this.querySelector('#ws-attachments');
    if (!box) return;
    box.innerHTML = this.pendingImages.map((img, i) =>
      `<div class="ws-attach-thumb"><img src="${img.previewUrl}" alt="" /><button type="button" class="ws-attach-remove" data-action="remove-attachment" data-idx="${i}" aria-label="Remove">✕</button></div>`,
    ).join('');
  }

  async onFilesSelected(e) {
    const files = [...(e.target.files || [])].slice(0, 3);
    e.target.value = '';
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 8 * 1024 * 1024) {
        BlingAIRender.toast('Image too large (max 8 MB)');
        continue;
      }
      const previewUrl = URL.createObjectURL(file);
      const placeholder = { r2_key: null, previewUrl, uploading: true };
      this.pendingImages.push(placeholder);
      this.renderAttachmentPreviews();

      try {
        const presign = await fetch(`${this.config.apiBase}/api/uploads/presigned`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_name: file.name, mime_type: file.type, file_size: file.size }),
        });
        const pj = await presign.json();
        if (pj.error) throw new Error(pj.error);

        const put = await fetch(pj.data.upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!put.ok) throw new Error('Upload failed: ' + put.status);

        placeholder.r2_key = pj.data.r2_key;
        placeholder.uploading = false;
      } catch (err) {
        console.warn('[BlingAI] upload', err);
        BlingAIRender.toast('Upload failed: ' + (err.message || 'unknown error'));
        this.pendingImages = this.pendingImages.filter((p) => p !== placeholder);
        this.renderAttachmentPreviews();
      }
    }
  }

  // ───── Send ──────────────────────────────────────────────────
  async send() {
    if (this.sending) return;
    const input = this.querySelector('#ws-input');
    const text = input?.value?.trim() || '';
    const ready = this.pendingImages.filter((p) => p.r2_key && !p.uploading);
    if (!text && !ready.length) return;

    this.sending = true;
    const sendBtn = this.querySelector('#ws-send');
    if (sendBtn) sendBtn.disabled = true;

    const imageKeys = ready.map((p) => p.r2_key);
    const firstPreview = ready[0]?.previewUrl;
    this.pendingImages = this.pendingImages.filter((p) => p.uploading); // keep still-uploading ones (rare)
    this.renderAttachmentPreviews();

    this.messages.push({ role: 'user', content: text || '[Image attached]', imagePreviewUrl: firstPreview });
    input.value = '';
    this._autosize(input);
    await this.renderMessages();

    const assistantIdx = this.messages.length;
    this.messages.push({ role: 'assistant', content: '' });

    const box = this.querySelector('#ws-msgs');
    if (box) {
      const typing = document.createElement('div');
      typing.className = 'ws-typing';
      typing.id = 'ws-typing';
      typing.textContent = 'Thinking…';
      box.appendChild(typing);
      box.scrollTop = box.scrollHeight;
    }

    try {
      const res = await fetch(`${this.config.apiBase}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: this.sessionId,
            conversation_id: this.conversationId,
            message: text,
            product_id: this.activeProduct?.id || this.selectedProduct?.id,
            page_context: 'workspace',
            session_type: 'workspace',
            stream: true,
            image_r2_keys: imageKeys.length ? imageKeys : undefined,
          }),
      });

      if (!res.ok || !res.body) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `Request failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let full = '';
      let lastRender = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.delta) {
              full += data.delta;
              this.messages[assistantIdx].content = full;
              const now = Date.now();
              if (now - lastRender > 80) {
                lastRender = now;
                document.getElementById('ws-typing')?.remove();
                await this.renderMessages();
              }
            }
            if (data.done && data.conversation_id) {
              this.conversationId = data.conversation_id;
              localStorage.setItem('bling_conversation_id', this.conversationId);
            }
          } catch {
            // skip malformed SSE
          }
        }
      }
      document.getElementById('ws-typing')?.remove();
      this.messages[assistantIdx].content = full;
      await this.renderMessages();
    } catch (err) {
      document.getElementById('ws-typing')?.remove();
      this.messages[assistantIdx].content = `**Error:** ${err.message || 'Something went wrong. Please try again.'}`;
      await this.renderMessages();
    } finally {
      this.sending = false;
      if (sendBtn) sendBtn.disabled = false;
    }
  }
}

customElements.define('bling-ai-workspace', BlingAIWorkspace);
